/**
 * Impersonation token handover to main-frontend (MAP-4314).
 *
 * The Firebase custom token used to impersonate a user can sign in as that user for
 * one hour, so it must not travel in the /login URL, where Datadog, Jitsu, GTM, gtag
 * and the browser history record it (MAP-4287). Instead the main-frontend tab is
 * opened at /login?impersonate, announces itself to its window.opener (this window)
 * once it has no session, and gets the token back through postMessage.
 *
 * Keep the message types in sync with main-frontend's impersonate-handshake.ts.
 */

export const IMPERSONATE_READY_MSG = 'maplabs:impersonate-ready';
export const IMPERSONATE_TOKEN_MSG = 'maplabs:impersonate-token';

export type HandshakeWindow = Pick<Window, 'addEventListener'>;

/**
 * Answers the ready messages of the tabs opened to impersonate, for as long as this
 * window lives. A tab can ask more than once: it reloads after signing out a previous
 * session, and again when another tab of the same domain signs out. The token is not
 * single use, so every ask is answered, like a reload of the old ?impersonate_token=
 * URL did.
 *
 * Every tab of a domain gets the token of the latest impersonation started on that
 * domain. Firebase keeps one session per domain, shared by its tabs, so an older tab
 * reloading must log in as the latest impersonated user, not take the session back.
 */
export class ImpersonateTokenResponder {
  // Origin each opened tab was opened on, weak so closed tabs are dropped
  private _tabOrigins   = new WeakMap<Window, string>();
  private _latestTokens = new Map<string, string>();
  private _listening    = false;

  constructor(private _win : HandshakeWindow) {}

  /**
   * Answers the ready messages of tab with the latest token of targetOrigin, which is
   * token until another impersonation starts on that origin. Only a ready message
   * coming from an opened tab (not from any other window) and from the origin it was
   * opened on is answered, and the token is sent with that origin so the browser drops
   * it if the tab navigated elsewhere.
   */
  register(tab          : Window,
           targetOrigin : string,
           token        : string) : void {
    this._tabOrigins.set(tab, targetOrigin);
    this._latestTokens.set(targetOrigin, token);
    if (!this._listening) {
      this._win.addEventListener('message', (event : MessageEvent) => this._onMessage(event));
      this._listening = true;
    }
  }

  private _onMessage(event : MessageEvent) : void {
    const tab    = event.source as Window | null;
    const origin = tab ? this._tabOrigins.get(tab) : undefined;
    if (!origin || event.origin !== origin || event.data?.type !== IMPERSONATE_READY_MSG)
      return;
    tab.postMessage({ type: IMPERSONATE_TOKEN_MSG, token: this._latestTokens.get(origin) }, origin);
  }
}

export const impersonateTokenResponder = new ImpersonateTokenResponder(window);
